/**
 * The lifecycle of an agent run, in three classes. The single source for the
 * y.enum(), the SQL CHECK (`agent_runs_status_check`), the trigger counters and
 * the frontend's polling. The full list is built from the classes, so a status
 * cannot be added without being classified.
 *
 * - active: has or will get a worker. Only `running` is subject to the stale
 *   reaper.
 * - waiting: no worker; the turn is checkpointed in `chat_parked_turns` and
 *   resumes when its `wait_reason` resolves, or ends when its TTL expires. Never
 *   reaped as stale.
 * - terminal: immutable, and the run's `result` is written.
 */
export const ACTIVE_RUN_STATUSES = ["queued", "running"] as const;
export const WAITING_RUN_STATUSES = ["waiting"] as const;
export const TERMINAL_RUN_STATUSES = [
	"succeeded",
	"failed",
	"rejected",
	"cancelled",
] as const;

export const AGENT_RUN_STATUSES = [
	...ACTIVE_RUN_STATUSES,
	...WAITING_RUN_STATUSES,
	...TERMINAL_RUN_STATUSES,
] as const;

export type AgentRunStatus = (typeof AGENT_RUN_STATUSES)[number];

/**
 * `rejected` means a human declined at least one confirmable call; nothing went
 * wrong. `cancelled` means someone stopped the run.
 */
export type TerminalRunStatus = (typeof TERMINAL_RUN_STATUSES)[number];
export type OpenRunStatus = Exclude<AgentRunStatus, TerminalRunStatus>;

/**
 * Why a `waiting` run waits. `approval` is a confirmable call parked for a human;
 * `children` and `lead` are reserved for delegation and never written yet.
 */
export const AGENT_RUN_WAIT_REASONS = ["approval", "children", "lead"] as const;
export type AgentRunWaitReason = (typeof AGENT_RUN_WAIT_REASONS)[number];

export const isTerminalRunStatus = (
	status: AgentRunStatus,
): status is TerminalRunStatus =>
	(TERMINAL_RUN_STATUSES as readonly AgentRunStatus[]).includes(status);

export const isOpenRunStatus = (
	status: AgentRunStatus,
): status is OpenRunStatus => !isTerminalRunStatus(status);

/** Whether a run is parked on a call a human has to decide. */
export const isAwaitingApproval = (run: {
	status: AgentRunStatus;
	waitReason?: AgentRunWaitReason | null;
}): boolean => run.status === "waiting" && run.waitReason === "approval";
